/**
 * Demo Review Panel
 *
 * Mock version of ReviewPanel for demo mode. Shows simulated LLM
 * draft output and lets users Accept/Edit/Discard without hitting
 * the real API. Calls completeReview() on the demo pipeline hook.
 */

import { useState } from 'react';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DemoReviewPanelProps {
  draftSummary: string;
  draftTags: string[];
  onReview: (
    action: 'accept' | 'edit' | 'discard',
    editedData?: { summary?: string; tags?: string[] }
  ) => void;
  className?: string;
}

export function DemoReviewPanel({
  draftSummary,
  draftTags,
  onReview,
  className,
}: DemoReviewPanelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState(draftSummary);
  const [editedTags, setEditedTags] = useState(draftTags.join(', '));
  // Track whether edits have been saved (for showing updated read-only view)
  const [hasSavedEdits, setHasSavedEdits] = useState(false);

  // Display values: show saved edits if any, otherwise show original draft
  const displaySummary = hasSavedEdits ? editedSummary : draftSummary;
  const displayTags = hasSavedEdits
    ? editedTags.split(',').map((t) => t.trim()).filter(Boolean)
    : draftTags;

  return (
    <div
      className={cn(
        'border border-accent-yellow/30 bg-accent-yellow/5 rounded-lg p-4',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 bg-accent-yellow rounded-full animate-pulse" />
        <h3 className="text-sm font-semibold text-text-primary">
          Review LLM Output
        </h3>
        <span className="text-xs text-text-muted ml-auto">
          Draft awaiting your confirmation
        </span>
      </div>

      {/* Draft Summary */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-text-muted mb-1">
          Summary
        </label>
        {isEditing ? (
          <textarea
            value={editedSummary}
            onChange={(e) => setEditedSummary(e.target.value)}
            className="w-full bg-bg-primary border border-border rounded-md px-3 py-2 text-xs text-text-primary resize-y min-h-[80px] focus:outline-hidden focus:ring-1 focus:ring-accent-yellow"
            rows={4}
          />
        ) : (
          <div className="bg-bg-primary border border-border rounded-md px-3 py-2 text-xs text-text-primary whitespace-pre-wrap">
            {displaySummary}
          </div>
        )}
      </div>

      {/* Draft Tags */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-text-muted mb-1">
          Tags
        </label>
        {isEditing ? (
          <input
            type="text"
            value={editedTags}
            onChange={(e) => setEditedTags(e.target.value)}
            className="w-full bg-bg-primary border border-border rounded-md px-3 py-1.5 text-xs text-text-primary focus:outline-hidden focus:ring-1 focus:ring-accent-yellow"
            placeholder="tag1, tag2, tag3"
          />
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {displayTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-accent-blue/15 text-accent-blue"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Demo badge */}
      <div className="mb-3 text-[10px] text-text-muted bg-bg-primary rounded-sm px-2 py-1.5 border border-border/50">
        Demo mode — corrections improve future LLM responses (RLHF feedback loop)
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            if (hasSavedEdits) {
              const parsedTags = editedTags
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean);
              onReview('edit', {
                summary: editedSummary,
                tags: parsedTags,
              });
            } else {
              onReview('accept');
            }
          }}
          disabled={isEditing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-green/15 text-accent-green rounded-md text-xs font-medium hover:bg-accent-green/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Accept
        </button>

        <button
          type="button"
          onClick={() => {
            if (isEditing) {
              // Save edits locally — don't submit yet
              setIsEditing(false);
              setHasSavedEdits(true);
            } else {
              setIsEditing(true);
            }
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-yellow/15 text-accent-yellow rounded-md text-xs font-medium hover:bg-accent-yellow/25 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          {isEditing ? 'Save Edits' : 'Edit'}
        </button>

        {isEditing && (
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              if (!hasSavedEdits) {
                setEditedSummary(draftSummary);
                setEditedTags(draftTags.join(', '));
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-bg-hover text-text-muted rounded-md text-xs font-medium hover:bg-border hover:text-text-primary transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Cancel
          </button>
        )}

        <button
          type="button"
          onClick={() => onReview('discard')}
          disabled={isEditing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-red/15 text-accent-red rounded-md text-xs font-medium hover:bg-accent-red/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ml-auto"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Discard
        </button>
      </div>
    </div>
  );
}
